import { ToolSummary, ToolSearch, ToolButton, ToolEmpty } from "./ToolWorkspace";
import { HeaderIcon } from "./AppHeader";
import { useState, useEffect, useRef, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { COLORS_DARK_MODE_DEFAULT, COLORS_DEFAULT } from "@blocknote/core";
import { pt as dicionarioPt } from "@blocknote/core/locales";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";

// Menu de blocos, tooltips da barra de formatação, atalhos etc. — tudo em
// pt-BR (dicionário oficial do BlockNote, cobre core + react + mantine).
import { C } from "../constants/theme";
import { useAppTheme } from "../hooks/useAppTheme";
import { fbAdd, fbUpdate, fbDelete, fbWatch } from "../hooks/useFirebase";
import { toast } from "../hooks/useToast";
import { useConfirm } from "../hooks/useConfirm";

const fns = getFunctions(undefined, "us-central1");
const cfOrganizar = httpsCallable(fns, "organizarAnotacao");

// Editor em blocos estilo Notion (BlockNote: github.com/TypeCellOS/BlockNote),
// com tema escuro alinhado à paleta do app.
const blockNoteTheme = {
  colors: {
    editor:   { text: C.text,  background: C.panel },
    menu:     { text: C.text,  background: C.card },
    tooltip:  { text: C.text,  background: C.card },
    hovered:  { text: C.text,  background: C.border },
    selected: { text: C.text,  background: "color-mix(in srgb, var(--pc-ui-00d4aa, #00d4aa) 20%, transparent)" },
    disabled: { text: C.muted, background: C.panel },
    shadow: "#000000",
    border: C.border,
    sideMenu: C.muted,
    highlights: COLORS_DARK_MODE_DEFAULT,
  },
  borderRadius: 10,
  fontFamily: "inherit",
};

const BLOCO_VAZIO = [{ type: "paragraph", content: [] }];

function paraData(v) {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  return new Date(v);
}
function estaExpirada(nota) {
  const d = paraData(nota.expiraEm);
  return d ? d.getTime() < Date.now() : false;
}

// Compat: anotações criadas antes do editor em blocos (campos texto/itens
// simples) abrem convertidas — sem precisar migrar nada no Firestore.
function blocosIniciais(nota) {
  if (nota?.conteudo?.length) return nota.conteudo;
  if (nota?.tipo === "lista" && nota.itens?.length) {
    return nota.itens.map(it => ({
      type: "checkListItem",
      props: { checked: !!it.feito },
      content: [{ type: "text", text: it.texto || "", styles: {} }],
    }));
  }
  if (nota?.texto) {
    return nota.texto.split("\n").filter(Boolean).map(linha => ({
      type: "paragraph",
      content: [{ type: "text", text: linha, styles: {} }],
    }));
  }
  return BLOCO_VAZIO;
}

function extrairTexto(blocks) {
  const linhas = [];
  const percorrer = arr => {
    for (const b of arr || []) {
      const t = (b.content || []).filter(c => c.type === "text").map(c => c.text).join("");
      if (t) linhas.push(t);
      if (b.children?.length) percorrer(b.children);
    }
  };
  percorrer(blocks);
  return linhas.join("\n").trim();
}

// Uma instância de editor por nota (key no componente pai garante remount
// ao trocar de nota). Autosalva com debounce; cria o doc no primeiro texto
// digitado e depois só atualiza.
function NotaEditor({ nota, onCriar, tituloManual }) {
  const { theme } = useAppTheme();
  const editor = useCreateBlockNote({ initialContent: blocosIniciais(nota), dictionary: dicionarioPt });
  const idRef = useRef(nota?.id || null);
  const timerRef = useRef(null);
  // Depois que o usuário edita o título manualmente (NotasIA.renomearNota),
  // o autosave do corpo para de sobrescrevê-lo com a 1ª linha do texto.
  // `tituloManual` vem de notaAtual no pai (não de `nota`, que fica null
  // durante toda a sessão de uma nota "nova" recém-criada) pra refletir o
  // valor em tempo real do Firestore mesmo pra notas criadas nesta sessão.
  const tituloManualRef = useRef(!!tituloManual);
  const [organizando, setOrganizando] = useState(false);
  const [saveState, setSaveState] = useState("As alterações são salvas automaticamente");

  useEffect(() => { tituloManualRef.current = !!tituloManual; }, [tituloManual]);

  const salvar = useCallback(async (blocks) => {
    const texto = extrairTexto(blocks);
    if (!idRef.current && !texto) return;
    const preview = texto.replace(/\n+/g, " · ").slice(0, 90);
    const payload = { conteudo: blocks, preview };
    if (!tituloManualRef.current) {
      payload.titulo = texto.split("\n")[0].slice(0, 40) || "Sem título";
    }
    setSaveState("Salvando…");
    try {
      if (!idRef.current) {
        const ref = await fbAdd("anotacoes", { titulo: payload.titulo || "Sem título", ...payload, expiraEm: null });
        idRef.current = ref.id;
        onCriar(ref.id);
      } else {
        await fbUpdate("anotacoes", idRef.current, payload);
      }
      setSaveState("Todas as alterações foram salvas");
    } catch {
      setSaveState("Não foi possível salvar. Edite o texto para tentar novamente.");
      // Nota pode ter sido excluída entre o agendamento e o disparo do
      // autosave (ex.: exclusão enquanto o debounce de 700ms ainda corria).
    }
  }, [onCriar]);

  const agendarSalvar = useCallback(() => {
    setSaveState("Alterações pendentes…");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => salvar(editor.document), 700);
  }, [editor, salvar]);

  useEffect(() => () => { clearTimeout(timerRef.current); salvar(editor.document); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const inserirBloco = (tipo) => {
    const bloco = editor.getTextCursorPosition().block;
    const vazio = extrairTexto([bloco]).trim() === "";
    if (vazio) editor.updateBlock(bloco, { type: tipo });
    else editor.insertBlocks([{ type: tipo }], bloco, "after");
    editor.focus();
    agendarSalvar();
  };

  const organizarComIA = async () => {
    const texto = extrairTexto(editor.document);
    if (!texto.trim()) return;
    setOrganizando(true);
    try {
      const r = await cfOrganizar({ texto });
      const { tipo, itens, texto: textoLimpo } = r.data || {};
      const novosBlocos = tipo === "lista"
        ? (itens || []).map(t => ({ type: "checkListItem", content: [{ type: "text", text: t, styles: {} }] }))
        : (textoLimpo || texto).split("\n").filter(Boolean).map(l => ({ type: "paragraph", content: [{ type: "text", text: l, styles: {} }] }));
      editor.replaceBlocks(editor.document, novosBlocos.length ? novosBlocos : BLOCO_VAZIO);
      agendarSalvar();
      toast(tipo === "lista" ? "✨ Organizada como lista!" : "✨ Nota organizada!");
    } catch (e) { toast(e.message || "Não foi possível organizar agora.", "error"); }
    setOrganizando(false);
  };

  const blocoBtn = { padding: "8px 14px", borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: "pointer", background: C.panel, border: "1px solid " + C.border, color: C.text };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={() => inserirBloco("checkListItem")} style={blocoBtn} title="Lista de verificação">☑️ Checklist</button>
        <button onClick={() => inserirBloco("numberedListItem")} style={blocoBtn} title="Lista numerada">1. 2. 3.</button>
        <button onClick={() => inserirBloco("bulletListItem")} style={blocoBtn} title="Lista com marcadores">• Marcadores</button>
      </div>
      <p className="tool-save-status" role="status">{saveState}</p>
      <div style={{ border: "1px solid " + C.border, borderRadius: 12, overflow: "hidden", background: C.panel, minHeight: 300 }}>
        <BlockNoteView editor={editor} theme={{ ...blockNoteTheme, colors: { ...blockNoteTheme.colors, highlights: theme === "light" ? COLORS_DEFAULT : COLORS_DARK_MODE_DEFAULT } }} onChange={agendarSalvar} />
      </div>
      <button onClick={organizarComIA} disabled={organizando}
        style={{ alignSelf: "flex-start", padding: "10px 18px", borderRadius: 10, fontSize: 13.5, fontWeight: 800, cursor: "pointer", background: "linear-gradient(135deg,var(--pc-ui-f59e0b, #f59e0b),#fbbf24)", border: "none", color: "#1a1300", opacity: organizando ? .6 : 1 }}>
        {organizando ? "⏳ Organizando..." : "✨ Organizar com IA"}
      </button>
    </div>
  );
}

// Bloco de notas — editor em blocos (texto, checklist, títulos etc., como
// no Notion) com autosave. Data de expiração é opcional e resolvida só pelo
// sistema (comparação de data no cliente, sem IA): ao passar, a nota some
// da lista principal e cai sozinha na faixa "Expiradas".
export function NotasIA() {
  const [busca, setBusca] = useState("");
  const [notas, setNotas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [abertaId, setAbertaId] = useState(null); // id de nota existente, "nova", ou null (lista)
  const [criadaId, setCriadaId] = useState(null); // id real assim que uma nota nova é salva pela 1ª vez
  const [mostrarExpiradas, setMostrarExpiradas] = useState(false);
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [tituloCampo, setTituloCampo] = useState("");
  const [confirmar, modalConfirm] = useConfirm();

  useEffect(() => {
    const unsub = fbWatch("anotacoes", d => {
      setNotas([...d].sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0)));
      setCarregando(false);
    });
    return () => unsub && unsub();
  }, []);

  const notasAtivas = notas.filter(n => !estaExpirada(n));
  const notasExpiradas = notas.filter(estaExpirada);
  const notaClicada = abertaId && abertaId !== "nova" ? notas.find(n => n.id === abertaId) : null;
  const notaAtualId = abertaId === "nova" ? criadaId : abertaId;
  const notaAtual = notas.find(n => n.id === notaAtualId);

  // Mantém o campo de título sincronizado com o Firestore (auto-título
  // enquanto o usuário digita o corpo) sem atrapalhar quem está digitando
  // o título nesse instante.
  useEffect(() => {
    if (!editandoTitulo) setTituloCampo(notaAtual?.titulo || "");
  }, [notaAtual?.titulo, notaAtual?.id, editandoTitulo]);

  const novaNota = () => { setAbertaId("nova"); setCriadaId(null); };
  const voltar = () => { setAbertaId(null); setCriadaId(null); };
  const onCriar = useCallback(id => setCriadaId(id), []);

  const renovarNota = async (nota, dias) => {
    await fbUpdate("anotacoes", nota.id, { expiraEm: dias ? new Date(Date.now() + dias * 86400000) : null });
    toast(dias ? `Renovada por +${dias}d` : "Data de expiração removida");
  };

  const definirExpiracao = async (nota, yyyyMmDd) => {
    await fbUpdate("anotacoes", nota.id, { expiraEm: new Date(yyyyMmDd + "T23:59:59") });
    toast("Data de expiração definida");
  };

  const renomearNota = async (nota, novoTitulo) => {
    const titulo = novoTitulo.trim() || "Sem título";
    if (titulo === (nota.titulo || "")) return;
    await fbUpdate("anotacoes", nota.id, { titulo, tituloManual: true });
  };

  const excluirNota = async (nota) => {
    if (!await confirmar(`Excluir "${nota.titulo || "essa anotação"}"?`)) return;
    await fbDelete("anotacoes", nota.id);
    // notaAtualId (não abertaId) cobre também o caso de excluir uma nota
    // "nova" recém-criada nesta sessão — abertaId continua "nova" até
    // Voltar ser clicado, nunca vira o id real do Firestore.
    if (notaAtualId === nota.id) voltar();
  };

  const matches = n => [n.titulo,n.preview,n.texto].join(" ").toLowerCase().includes(busca.toLowerCase().trim());
  const cards = (items, expired=false) => items.filter(matches).map(n => <article key={n.id} className="tool-note-card">
    <span className="tool-item-mark"><HeaderIcon name="notas" size={22} /></span>
    <button type="button" className="tool-note-open" onClick={()=>setAbertaId(n.id)}><strong>{n.titulo||"Sem título"}</strong><span>{n.preview||n.texto||"Abrir anotação"}</span>{n.expiraEm&&<span>{expired?"Expirou em ":"Expira em "}{paraData(n.expiraEm)?.toLocaleDateString("pt-BR")}</span>}</button>
    <div className="tool-note-actions">{expired&&<ToolButton secondary onClick={()=>renovarNota(n,7)} aria-label={"Renovar "+n.titulo+" por 7 dias"}>+7 dias</ToolButton>}<ToolButton danger aria-label={"Excluir "+(n.titulo||"anotação")} onClick={()=>excluirNota(n)}>Excluir</ToolButton></div>
  </article>);
  return (<div className="tool-module tool-module--notas">
    {modalConfirm}
    {abertaId===null&&<>
      <ToolSummary items={[{label:"Anotações ativas",value:notasAtivas.length},{label:"Com prazo",value:notasAtivas.filter(n=>n.expiraEm).length},{label:"Expiradas",value:notasExpiradas.length}]} />
      <ToolSearch value={busca} onChange={setBusca} placeholder="Buscar nas anotações"><ToolButton onClick={novaNota}>+ Nova anotação</ToolButton></ToolSearch>
      {carregando?<ToolEmpty icon="notas" title="Carregando anotações…" />:!notasAtivas.filter(matches).length?<ToolEmpty icon="notas" title={busca?"Nenhuma anotação encontrada":"Dê um lugar para suas ideias"}>Escreva textos, monte checklists e use a IA para organizar suas notas.</ToolEmpty>:cards(notasAtivas)}
      {notasExpiradas.length>0&&<><ToolButton secondary aria-expanded={mostrarExpiradas} onClick={()=>setMostrarExpiradas(v=>!v)}>{mostrarExpiradas?"Ocultar":"Ver"} expiradas ({notasExpiradas.length})</ToolButton>{mostrarExpiradas&&cards(notasExpiradas,true)}</>}
    </>}
      {(abertaId === "nova" || notaClicada) && (
        <div className="tool-editor" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
            {notaAtual ? (
              <input aria-label="Título da anotação" value={tituloCampo}
                onFocus={() => setEditandoTitulo(true)}
                onChange={e => setTituloCampo(e.target.value)}
                onBlur={() => { setEditandoTitulo(false); renomearNota(notaAtual, tituloCampo); }}
                onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                placeholder="Sem título"
                title="Clique para editar o título"
                style={{ background: "none", border: "none", borderBottom: "1px dashed " + C.border, color: C.text, fontWeight: 800, fontSize: 17, outline: "none", flex: 1, minWidth: 0, padding: "2px 2px 4px" }} />
            ) : (
              <div style={{ color: C.text, fontWeight: 800, fontSize: 17 }}>Nova anotação</div>
            )}
            <button onClick={voltar} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13.5, fontWeight: 700, flexShrink: 0 }}>← Voltar</button>
          </div>

          <NotaEditor key={abertaId === "nova" ? "nova" : abertaId} nota={notaClicada} onCriar={onCriar} tituloManual={notaAtual?.tituloManual} />

          {notaAtual && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: estaExpirada(notaAtual) ? "#ef4444" : C.muted, flexWrap: "wrap" }}>
              {paraData(notaAtual.expiraEm)
                ? <span>⏰ {estaExpirada(notaAtual) ? "Expirou em" : "Expira em"} {paraData(notaAtual.expiraEm).toLocaleDateString("pt-BR")}</span>
                : <span>Sem data de expiração</span>}
              <input aria-label="Data de expiração" type="date" onChange={e => e.target.value && definirExpiracao(notaAtual, e.target.value)}
                style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 8, padding: "6px 8px", color: C.text, fontSize: 12.5, outline: "none" }} />
              {paraData(notaAtual.expiraEm) && (
                <button onClick={() => renovarNota(notaAtual, 0)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12 }}>remover data</button>
              )}
            </div>
          )}

          {notaAtual && (
            <button onClick={() => excluirNota(notaAtual)} style={{ alignSelf: "flex-start", background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 13.5, fontWeight: 700 }}>
              🗑️ Excluir anotação
            </button>
          )}
        </div>
      )}
    </div>
  );
}
